/**
 * ChartVizResult — Chart Visualization result renderer.
 *
 * Spec: v2-design/RESULT_RENDERING_v0_spec.md §3.
 *
 * Renders a single chart per spec §11 (no multi-tab chart1/chart2
 * scaffolding).
 *
 * Input is the verbatim backend chart_payload (whatever the
 * /Chart_Visualization API returned), enriched server-side with a
 * `request_context` block carrying ModelInstanceID + RowIdentifierIDs +
 * Timescales — the IDs the FE needs to fetch RowIdentifier formatting
 * info. That fetch stays client-side per spec §3.4's chosen v0 path,
 * rather than having the backend enrich the payload directly.
 *
 * Sizing: the card body grows to fit the chart + legend + axis labels.
 * Spec §3.5 originally called for a fixed 360px height with internal
 * scroll, but the legend below the chart pushed past that bound in
 * practice. Auto height is the v0 choice; revisit if a single result
 * card visually dwarfs surrounding chat content.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { find, findIndex, includes } from 'lodash';

import { dateFormats } from 'src/components/ScenarioManager/EditAssumptionsDetail/OutputsPanel/FormatChartPanel';
import {
  formatParameters,
  IFormatOutputParameters,
} from 'src/components/Visualization/formatParameters';
import { useData } from 'src/hooks/useData';
import { RowIdentifier, Timescale } from 'src/types/models';

import GenerateChartPanel, {
  chartTypes,
  chartDataTypes,
} from './charts/GenerateChartPanel';

interface ChartDataSeries {
  id?: number;
  scenario?: string;
  segment?: string;
  name?: string;
  values?: number[];
  visible?: boolean;
  output?: string;
}

interface ChartDataBlock {
  PreviewData?: ChartDataSeries[];
  PublishedData?: ChartDataSeries[];
  Dates?: Array<{ Date: string }>;
  ChartName?: string;
}

interface ChartPayload {
  // The forecast API wraps its chart response under `data`, so
  // chart_payload.data holds {PreviewData, PublishedData, Dates, ChartName}.
  data?: ChartDataBlock;
  Status?: unknown;
  // Server-side bundle of the IDs the FE needs to look up RowIdentifier
  // format metadata. Optional for compatibility — the renderer falls
  // back to default formatting when absent.
  request_context?: {
    model_instance_id?: number;
    row_identifier_ids?: number[];
    timescales?: string[];
  };
}

export interface ChartVizResultProps {
  chartPayload: ChartPayload;
  // When false, suppresses the per-scenario legend. v0 default true.
  showScenarios?: boolean;
  // Exec chart frame (2026-09-07): forwarded to GenerateChartPanel — see its prop comment.
  frameChart?: boolean;
}

const transformSeries = (
  series: ChartDataSeries[] | undefined
): ChartDataSeries[] =>
  (series ?? []).map((item) => ({ ...item, visible: true }));

export const ChartVizResult: React.FC<ChartVizResultProps> = ({
  chartPayload,
  showScenarios = true,
  frameChart,
}) => {
  const dataBlock = chartPayload.data ?? {};

  // Guard against empty/malformed payloads that would crash
  // GenerateChartPanel's internals. Done in the outer component so the
  // hook calls inside ChartVizInner aren't conditional.
  const hasDates = (dataBlock.Dates?.length ?? 0) > 0;
  const hasSeries =
    (dataBlock.PreviewData?.length ?? 0) > 0 ||
    (dataBlock.PublishedData?.length ?? 0) > 0;
  if (!hasDates || !hasSeries) {
    return (
      <Box
        sx={{
          height: 240,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          fontSize: 13,
          textAlign: 'center',
          px: 2,
        }}
      >
        <Box sx={{ fontWeight: 600, mb: 0.5 }}>Chart data unavailable</Box>
        <Box sx={{ fontSize: 12 }}>
          The backend returned a chart payload without data series or dates.
          Check the Agentic-API logs and the Chart Visualization API response
          for the active plan.
        </Box>
      </Box>
    );
  }

  return (
    <ChartVizInner
      chartPayload={chartPayload}
      showScenarios={showScenarios}
      frameChart={frameChart}
    />
  );
};

const ChartVizInner: React.FC<
  Required<Pick<ChartVizResultProps, 'chartPayload'>> & {
    showScenarios: boolean;
    frameChart?: boolean;
  }
> = ({ chartPayload, showScenarios, frameChart }) => {
  const dataBlock = chartPayload.data ?? {};
  const modelInstanceId = chartPayload.request_context?.model_instance_id;
  const rowIdentifierIds = chartPayload.request_context?.row_identifier_ids;
  const timescaleString = chartPayload.request_context?.timescales?.[0];

  // Local interactive state for the chart panel (range / type / data type
  // / legend visibility). Always single-chart in v0, so this is a flat
  // shape rather than per-tab state.
  const [chartData, setChartData] = useState<ChartDataBlock>({
    PreviewData: transformSeries(dataBlock.PreviewData),
    PublishedData: transformSeries(dataBlock.PublishedData),
    Dates: dataBlock.Dates ?? [],
  });

  const [selectedChartRange, setSelectedChartRange] = useState<
    [string, string] | null
  >(null);

  // The date picker clips as a DERIVED VIEW rather than by rewriting `chartData` in place (which is what
  // ScenarioManager's OutputsPanel does — it can afford to, because it keeps a pristine `refreshedChartData`
  // alongside to re-slice from). Here the two were the same object, so an in-place slice would discard the
  // out-of-range years permanently: widening the range again, or hitting the picker's Clear button, would have
  // nothing left to restore. Deriving keeps `chartData` authoritative and full-range, so every range change —
  // narrower, wider, or cleared — recomputes from the complete payload. Legend toggles still write to `chartData`
  // and flow through untouched, since visibility lives on the series, not on the date axis.
  //
  // Dates and each series' `values` are positional siblings (index i of values belongs to Dates[i]) — the same
  // contract the table and Excel export rely on — so one [start, end] index pair slices all of them.
  const visibleChartData = useMemo<ChartDataBlock>(() => {
    const dates = chartData.Dates ?? [];
    if (!selectedChartRange || dates.length === 0) return chartData;

    const [startRange, endRange] = selectedChartRange;
    const startIndex = findIndex(dates, (d) => d.Date === startRange);
    const endIndex = findIndex(dates, (d) => d.Date === endRange);
    // Unresolvable or inverted bounds -> show everything, never a blank chart. The picker itself prevents the
    // user from choosing an inverted range (min/max are bounded by the payload's own dates), so this is a
    // defensive fallback for a label that does not match the axis, not a user-facing path.
    if (startIndex === -1 || endIndex === -1 || startIndex > endIndex) {
      return chartData;
    }
    if (startIndex === 0 && endIndex === dates.length - 1) return chartData;

    const clipSeries = (
      series?: ChartDataSeries[]
    ): ChartDataSeries[] | undefined =>
      series?.map((s) =>
        Array.isArray(s.values)
          ? { ...s, values: s.values.slice(startIndex, endIndex + 1) }
          : s
      );

    return {
      ...chartData,
      Dates: dates.slice(startIndex, endIndex + 1),
      PublishedData: clipSeries(chartData.PublishedData),
      PreviewData: clipSeries(chartData.PreviewData),
    };
  }, [chartData, selectedChartRange]);

  // The panel's two data props are deliberately different objects:
  //   chartData          -> visibleChartData, the clipped view. Chart and table render from it, so both narrow.
  //   refreshedChartData -> chartData, the full-range state. The range picker takes its selectable bounds
  //                         (minRange / maxRange / dates) from this.
  // They must not be collapsed into one. Pointing the picker at the clipped view makes its own output its next
  // input: narrowing to 2027-2035 would leave 2027-2035 as the entire selectable range, stranding the trimmed-off
  // years with no way back, and hiding the Clear button (ChartRangePicker.showClearButton compares the current
  // value against min/max of the dates it was given, which would then always be equal).
  const refreshedChartData = chartData;
  const [selectedChartType, setSelectedChartType] = useState<string>(
    chartTypes[0].id
  );
  const [selectedChartDataType, setSelectedChartDataType] = useState<string>(
    chartDataTypes[0].id
  );

  // Fetches the model's full RowIdentifier set; narrowed below to the
  // one matching our payload's Output RowIdentifier ID, to derive
  // formatKey + y-axis title.
  const { data } = useData<{ rowIdentifiers?: RowIdentifier[] }>(
    () => ({
      rowIdentifiers: modelInstanceId
        ? `/instances/${modelInstanceId}/row_identifiers`
        : undefined,
    }),
    [modelInstanceId]
  );

  const selectedRowIdentifier = React.useMemo(() => {
    if (!data?.rowIdentifiers || !rowIdentifierIds) return undefined;
    return data.rowIdentifiers.find(
      (r) => rowIdentifierIds.includes(r.id) && r.Type === 'Output'
    );
  }, [data?.rowIdentifiers, rowIdentifierIds]);

  const formatKey = selectedRowIdentifier?.FormatKey ?? 0;
  const formatSpec = find(formatParameters, { formatKey }) as
    | IFormatOutputParameters
    | undefined;
  const selectedPrefixValue = formatSpec?.numberType === '$' ? '$' : '';
  const selectedSuffixValue = formatSpec?.numberType === 'Percent' ? '%' : '';
  const magnitudeTruncation = (formatSpec?.magnitudeTruncation as number) ?? 0;

  const yAxisTitle =
    selectedRowIdentifier?.Name ?? dataBlock.PublishedData?.[0]?.output ?? '';

  const handleChangeChartRange = useCallback(
    (startRange: string, endRange: string) => {
      setSelectedChartRange([startRange, endRange]);
    },
    []
  );

  const handleChangeChartType = useCallback((next: string) => {
    setSelectedChartType(next);
  }, []);

  const handleChangeChartDataType = useCallback((next: string) => {
    setSelectedChartDataType(next);
  }, []);

  const handleLegendChange = useCallback((id: number, visible: boolean) => {
    setChartData((prev) => ({
      Dates: prev.Dates,
      PublishedData: (prev.PublishedData ?? []).map((item) =>
        item.id === id ? { ...item, visible } : item
      ),
      PreviewData: (prev.PreviewData ?? []).map((item) =>
        item.id === id ? { ...item, visible } : item
      ),
    }));
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <ChartVizErrorBoundary>
        <GenerateChartPanel
          chartData={visibleChartData as never}
          refreshedChartData={refreshedChartData as never}
          timescale={(timescaleString as Timescale) ?? Timescale.Yearly}
          formatKey={formatKey}
          yAxisTitle={yAxisTitle}
          chartTitle={dataBlock.ChartName ?? ''}
          prefix={selectedPrefixValue}
          suffix={selectedSuffixValue}
          magnitudeTruncation={magnitudeTruncation}
          decimalPlaces={2}
          chartDateType={
            includes(
              [Timescale.Weekly, Timescale.Monthly],
              timescaleString as Timescale
            )
              ? dateFormats(timescaleString as Timescale)[0] ?? ''
              : ''
          }
          selectedChartRange={selectedChartRange}
          selectedChartType={selectedChartType}
          selectedChartDataType={selectedChartDataType}
          handleChangeChartRange={handleChangeChartRange}
          handleChangeChartType={handleChangeChartType}
          handleChangeChartDataType={handleChangeChartDataType}
          handleLegendChange={handleLegendChange}
          isPanelCollapsed={false}
          showScenarios={showScenarios}
          frameChart={frameChart}
        />
      </ChartVizErrorBoundary>
    </Box>
  );
};

/**
 * Local error boundary so a crash inside GenerateChartPanel doesn't
 * propagate up to the app-wide ErrorBoundary in App.tsx — which would
 * replace the entire app (header, nav, everything) with "Something
 * went wrong." Contains the failure to this one chart result instead,
 * so the rest of the chat and app stay usable when a payload is
 * malformed.
 */
class ChartVizErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ChartVizResult] render failed', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            fontSize: 13,
            textAlign: 'center',
            px: 2,
          }}
        >
          <Box sx={{ fontWeight: 600, mb: 0.5 }}>Chart render failed</Box>
          <Box sx={{ fontSize: 12 }}>
            {this.state.error.message}. See the console for the full error and
            the [ChartVizResult] payload log.
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}
